<div class="container-margin import-flow" [attr.aria-busy]="!!processing">
  <ol
    class="import-stages"
    [attr.aria-label]="translationService.getImportProductsText()"
  >
    <li
      [class.current]="stage === 'upload'"
      [attr.aria-current]="stage === 'upload' ? 'step' : null"
    >
      <span>1</span>{{ translationService.getImportUploadText() }}
    </li>
    <li
      [class.current]="stage === 'review'"
      [attr.aria-current]="stage === 'review' ? 'step' : null"
    >
      <span>2</span>{{ translationService.getImportReviewText() }}
    </li>
    <li
      [class.current]="stage === 'complete'"
      [attr.aria-current]="stage === 'complete' ? 'step' : null"
    >
      <span>3</span>{{ translationService.getImportCompleteText() }}
    </li>
  </ol>
  <h2 #stageHeading tabindex="-1">{{ stageTitle }}</h2>
  @if (operationError) {
    <p class="operation-error" role="alert">{{ operationError }}</p>
  }
  <p class="processing-status" role="status">
    @if (processing === 'validation') {
      {{ translationService.getImportValidatingText() }}
    }
    @if (processing === 'confirmation') {
      {{ translationService.getImportConfirmingText() }}
    }
    @if (processing === 'download') {
      {{ translationService.getImportDownloadingText() }}
    }
  </p>
  <form
    #uploadForm
    [formGroup]="importForm"
    (ngSubmit)="onSubmit()"
    [hidden]="stage !== 'upload'"
  >
    <fieldset [disabled]="!!processing">
      <div class="upload-main">
        <p>{{ translationService.getFieldRequiredIndicatorText() }}</p>
        <p>{{ translationService.getProductCsvTemplateSelectionMessage() }}</p>
        <div class="template-controls">
          <mat-form-field appearance="outline">
            <mat-label>{{
              translationService.getCsvTemplateNameText()
            }}</mat-label>
            <mat-select formControlName="template" required>
              <mat-option value="marketplace">{{
                translationService.getMarketplaceImportTemplateText()
              }}</mat-option>
              @for (template of templates; track template.id) {
                <mat-option [value]="template.id">{{
                  template.name
                }}</mat-option>
              }
            </mat-select>
          </mat-form-field>
          <button
            mat-stroked-button
            type="button"
            (click)="downloadTemplate()"
            [disabled]="!!processing || !importForm.value.template"
          >
            <mat-icon>download</mat-icon
            >{{ translationService.getDownloadText() }}
          </button>
        </div>
        <p>{{ translationService.getImportCsvFileHelpText() }}</p>
        <div class="file-group">
          <label for="productImportFile"
            >{{ translationService.getProductImportFileText() }}
            <span aria-hidden="true">*</span></label
          >
          <input
            id="productImportFile"
            #productImportFile
            type="file"
            required
            accept=".csv"
            (change)="onFileChange($event, 'productImportFile')"
          />
          @if (selectedFiles('productImportFile').length) {
            <ul
              class="selected-files"
              [attr.aria-label]="
                translationService.getImportSelectedFilesText()
              "
            >
              @for (file of selectedFiles('productImportFile'); track $index) {
                <li>{{ file.name }}</li>
              }
            </ul>
            <button
              mat-button
              type="button"
              (click)="removeFiles('productImportFile', productImportFile)"
              [disabled]="!!processing"
              [attr.aria-label]="
                translationService.getImportRemoveFilesText(
                  translationService.getProductImportFileText()
                )
              "
            >
              {{ translationService.getRemoveText() }}
            </button>
          }
        </div>
      </div>
      <details class="upload-extra">
        <summary>
          {{ translationService.getImportAdditionalHelpText() }}
        </summary>
        @if (importForm.value.template === 'marketplace') {
          <p>{{ translationService.getMarketplaceImportHelpText() }}</p>
        }
        <h3>{{ translationService.getImportColumnGuideText() }}</h3>
        <p>{{ translationService.getMarketplaceImportValuesText() }}</p>
        <p>{{ translationService.getImportCustomFieldsHelpText() }}</p>
        <p>{{ translationService.getMarketplaceImportPrivacyText() }}</p>
        <p>{{ translationService.getTagsDelimiterInfoText() }}</p>
        <h3>{{ translationService.getOptionalImportFilesText() }}</h3>
        <div class="file-group">
          <label for="productPictures">{{
            translationService.getPicturesText()
          }}</label>
          <input
            id="productPictures"
            #productPictures
            type="file"
            multiple
            accept="image/png, image/jpg, image/jpeg"
            (change)="onFileChange($event, 'productPictures')"
          />
          @if (selectedFiles('productPictures').length) {
            <ul
              class="selected-files"
              [attr.aria-label]="
                translationService.getImportSelectedFilesText()
              "
            >
              @for (file of selectedFiles('productPictures'); track $index) {
                <li>{{ file.name }}</li>
              }
            </ul>
            <button
              mat-button
              type="button"
              (click)="removeFiles('productPictures', productPictures)"
              [disabled]="!!processing"
              [attr.aria-label]="
                translationService.getImportRemoveFilesText(
                  translationService.getPicturesText()
                )
              "
            >
              {{ translationService.getRemoveText() }}
            </button>
          }
        </div>
        <div class="file-group">
          <label for="safetyDataSheets">{{
            translationService.getSafetyDataSheetsText()
          }}</label>
          <input
            id="safetyDataSheets"
            #safetyDataSheets
            type="file"
            multiple
            accept=".pdf"
            (change)="onFileChange($event, 'safetyDataSheets')"
          />
          @if (selectedFiles('safetyDataSheets').length) {
            <ul
              class="selected-files"
              [attr.aria-label]="
                translationService.getImportSelectedFilesText()
              "
            >
              @for (file of selectedFiles('safetyDataSheets'); track $index) {
                <li>{{ file.name }}</li>
              }
            </ul>
            <button
              mat-button
              type="button"
              (click)="removeFiles('safetyDataSheets', safetyDataSheets)"
              [disabled]="!!processing"
              [attr.aria-label]="
                translationService.getImportRemoveFilesText(
                  translationService.getSafetyDataSheetsText()
                )
              "
            >
              {{ translationService.getRemoveText() }}
            </button>
          }
        </div>
        <div class="file-group">
          <label for="recyclingInstructions">{{
            translationService.getRecyclingInstructionsText()
          }}</label>
          <input
            id="recyclingInstructions"
            #recyclingInstructions
            type="file"
            multiple
            accept=".pdf"
            (change)="onFileChange($event, 'recyclingInstructions')"
          />
          @if (selectedFiles('recyclingInstructions').length) {
            <ul
              class="selected-files"
              [attr.aria-label]="
                translationService.getImportSelectedFilesText()
              "
            >
              @for (
                file of selectedFiles('recyclingInstructions');
                track $index
              ) {
                <li>{{ file.name }}</li>
              }
            </ul>
            <button
              mat-button
              type="button"
              (click)="
                removeFiles('recyclingInstructions', recyclingInstructions)
              "
              [disabled]="!!processing"
              [attr.aria-label]="
                translationService.getImportRemoveFilesText(
                  translationService.getRecyclingInstructionsText()
                )
              "
            >
              {{ translationService.getRemoveText() }}
            </button>
          }
        </div>
        <div class="file-group">
          <label for="ingredientsFile">{{
            translationService.getIngredientsFileText()
          }}</label>
          <input
            id="ingredientsFile"
            #ingredientsFile
            type="file"
            accept=".csv"
            (change)="onFileChange($event, 'ingredientsFile')"
          />
          @if (selectedFiles('ingredientsFile').length) {
            <ul
              class="selected-files"
              [attr.aria-label]="
                translationService.getImportSelectedFilesText()
              "
            >
              @for (file of selectedFiles('ingredientsFile'); track $index) {
                <li>{{ file.name }}</li>
              }
            </ul>
            <button
              mat-button
              type="button"
              (click)="removeFiles('ingredientsFile', ingredientsFile)"
              [disabled]="!!processing"
              [attr.aria-label]="
                translationService.getImportRemoveFilesText(
                  translationService.getIngredientsFileText()
                )
              "
            >
              {{ translationService.getRemoveText() }}
            </button>
          }
        </div>
        <div class="file-group">
          <label for="resinsFile">{{
            translationService.getResinsFileText()
          }}</label>
          <input
            id="resinsFile"
            #resinsFile
            type="file"
            accept=".csv"
            (change)="onFileChange($event, 'resinsFile')"
          />
          @if (selectedFiles('resinsFile').length) {
            <ul
              class="selected-files"
              [attr.aria-label]="
                translationService.getImportSelectedFilesText()
              "
            >
              @for (file of selectedFiles('resinsFile'); track $index) {
                <li>{{ file.name }}</li>
              }
            </ul>
            <button
              mat-button
              type="button"
              (click)="removeFiles('resinsFile', resinsFile)"
              [disabled]="!!processing"
              [attr.aria-label]="
                translationService.getImportRemoveFilesText(
                  translationService.getResinsFileText()
                )
              "
            >
              {{ translationService.getRemoveText() }}
            </button>
          }
        </div>
        <div class="file-group">
          <label for="componentsFile">{{
            translationService.getComponentsFileText()
          }}</label>
          <input
            id="componentsFile"
            #componentsFile
            type="file"
            accept=".csv"
            (change)="onFileChange($event, 'componentsFile')"
          />
          @if (selectedFiles('componentsFile').length) {
            <ul
              class="selected-files"
              [attr.aria-label]="
                translationService.getImportSelectedFilesText()
              "
            >
              @for (file of selectedFiles('componentsFile'); track $index) {
                <li>{{ file.name }}</li>
              }
            </ul>
            <button
              mat-button
              type="button"
              (click)="removeFiles('componentsFile', componentsFile)"
              [disabled]="!!processing"
              [attr.aria-label]="
                translationService.getImportRemoveFilesText(
                  translationService.getComponentsFileText()
                )
              "
            >
              {{ translationService.getRemoveText() }}
            </button>
          }
        </div>
      </details>
      @if (reviewInvalidated) {
        <p role="status">
          {{ translationService.getImportReviewInvalidatedText() }}
        </p>
      }
      <div class="actions">
        <button
          mat-stroked-button
          type="button"
          (click)="reset()"
          [disabled]="!!processing"
        >
          {{ translationService.getResetText() }}
        </button>
        <button
          mat-flat-button
          type="submit"
          [disabled]="
            !!processing || importForm.invalid || !fileData['productImportFile']
          "
        >
          {{ translationService.getImportValidateFilesText() }}
        </button>
      </div>
    </fieldset>
  </form>
  @if (stage === 'review') {
    <app-review-products
      [importedProducts]="importResponse.productsToImport"
      [errorMap]="importResponse.errorMap"
    />
    <p>{{ translationService.getImportConfirmationHelpText() }}</p>
    <div class="actions">
      <button
        mat-stroked-button
        type="button"
        (click)="backToUpload()"
        [disabled]="!!processing"
      >
        {{ translationService.getImportBackToUploadText() }}
      </button>
      <button
        mat-flat-button
        type="button"
        (click)="importProducts()"
        [disabled]="!canConfirm"
      >
        {{ translationService.getImportConfirmText() }}
      </button>
    </div>
  }
  @if (stage === 'complete') {
    <div class="completion" role="status">
      <mat-icon>check_circle</mat-icon>
      <p>{{ translationService.getImportResultText(completedCount) }}</p>
    </div>
    <div class="actions">
      <a mat-flat-button [routerLink]="productsLink">{{
        translationService.getCompanyProductsText()
      }}</a>
      <button mat-stroked-button type="button" (click)="reset()">
        {{ translationService.getImportAnotherText() }}
      </button>
    </div>
  }
</div>
